import {
  BulkInvitationColumnSpec,
  BulkInvitationFieldOption,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

export const TEAM_MEMBER_ACCESS_TEMPLATE_KEY = "accessTemplateId";

/**
 * A firm's colleagues, listed by their team leader: everyone who will work and bill on the
 * client's matters.
 *
 * Built rather than declared, because one column's choices are loaded. The permission templates a
 * leader may apply are their own - the API refuses another user's - so the list cannot be known
 * until the caller's templates are.
 */
export default function createTeamMemberRosterColumns(
  accessTemplates: readonly BulkInvitationFieldOption[] = [],
): BulkInvitationColumnSpec {
  return {
    id: "team-members",
    noun: "colleague",
    templateFileName: "attorney-hub-team-members",
    templateSheetName: "Team members",
    fields: [
      {
        key: "email",
        header: "Email address",
        kind: "email",
        required: true,
        hint: "Must be on a domain your firm accepts invitations to.",
      },
      { key: "firstName", header: "First name", kind: "text", required: true },
      { key: "lastName", header: "Last name", kind: "text", required: true },
      {
        key: "title",
        header: "Title or responsibility",
        kind: "text",
        required: true,
        // Required here and optional on a single invitation, which is not an inconsistency: the
        // procedure this list serves depends on the client being able to find a billing
        // administrator in it without asking anybody.
        hint: "Name at least one billing administrator in the list, so the client knows who to ask about an invoice.",
      },
      {
        key: "requestedMarketplaceVisibility",
        header: "Publish profile to Marketplace",
        kind: "boolean",
        required: false,
        hint: "Applied when they accept. They own the profile from that moment and can change it.",
      },
      {
        key: TEAM_MEMBER_ACCESS_TEMPLATE_KEY,
        header: "Permission template",
        kind: "select",
        required: false,
        options: accessTemplates,
        // Kept out of the spreadsheet: its value is a GUID, and somebody filling a sheet in knows
        // their colleague's title but not a template's id. In the grid it is a list of names.
        omitFromSpreadsheet: true,
        hint: "Applied when they accept. Leave blank for the basic member permissions.",
      },
    ],
  };
}
